import type { ReactNode } from 'react';
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { CLOSING_BUTTON_DISABLED } from './closing-styles';

function PadBankLinkingCallout() {
  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-500/40 dark:bg-amber-950/40"
      role="note"
    >
      <div className="flex gap-2.5">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400"
          aria-hidden
        />
        <div className="min-w-0 text-sm leading-relaxed text-amber-950 dark:text-amber-100">
          <p className="font-semibold">Bank linking may be unavailable</p>
          <p className="mt-1 font-normal">
            Plaid cannot connect to every Canadian bank right now. If automatic linking fails, use{' '}
            <span className="font-medium">Send a blank agreement instead</span> below and enter your
            bank details on the DocuSign form.
          </p>
        </div>
      </div>
    </div>
  );
}

interface PadAgreementInstructionsProps {
  plaid?: ReactNode;
  completedCount?: number;
  onSendBlank?: () => Promise<void> | void;
  blankSending?: boolean;
}

const PAD_STEPS = [
  'Select your bank account',
  'Receive your pre-authorized debit agreement by email',
  'Sign the agreement. Check your email.',
] as const;

export function PadNumberedSteps({
  completedCount = 0,
  firstStepAction,
}: {
  completedCount?: number;
  firstStepAction?: ReactNode;
}) {
  return (
    <ol className="space-y-2.5">
      {PAD_STEPS.map((label, index) => {
        const done = index < completedCount;
        const showReload = index === PAD_STEPS.length - 1 && completedCount === index;
        const action = index === 0 && !done
          ? firstStepAction
          : showReload
            ? (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 dark:border-white/20 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
              >
                Signed agreement? Reload page
              </button>
            )
            : null;
        return (
          <li key={label} className={`flex gap-3 ${action ? 'items-start' : 'items-center'}`}>
            <span
              className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold leading-none text-white ${
                action ? 'mt-0.5' : ''
              } ${done ? 'bg-gray-300 dark:bg-white/20' : 'bg-red-700'}`}
            >
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <span
                className={`block text-sm font-medium leading-5 ${
                  done
                    ? 'text-gray-400 line-through decoration-gray-400 dark:text-gray-500 dark:decoration-gray-500'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {label}
              </span>
              {action ? <div className="mt-2">{action}</div> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function BlankPadAgreementButton({
  onSendBlank,
  sending = false,
}: {
  onSendBlank: () => Promise<void> | void;
  sending?: boolean;
}) {
  const [error, setError] = useState('');

  return (
    <div className="flex flex-col items-start gap-1.5">
      <button
        type="button"
        disabled={sending}
        onClick={() => {
          setError('');
          void Promise.resolve(onSendBlank()).catch((err: unknown) => {
            setError(err instanceof Error ? err.message : 'The blank agreement could not be sent.');
          });
        }}
        className="text-left text-sm font-medium text-gray-700 underline underline-offset-2 hover:text-gray-950 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-300 dark:hover:text-white"
      >
        {sending ? 'Sending agreement…' : 'Send a blank agreement instead'}
      </button>
      {error ? <p className="text-sm text-red-700 dark:text-red-400">{error}</p> : null}
    </div>
  );
}

export default function PadAgreementInstructions({
  plaid,
  completedCount = 0,
  onSendBlank,
  blankSending = false,
}: PadAgreementInstructionsProps) {
  const stepOpen = completedCount < 1;
  const selectBank = stepOpen
    ? (plaid ?? (
      <button type="button" className={CLOSING_BUTTON_DISABLED} disabled>
        Select your bank account to autofill the PAD form
      </button>
    ))
    : null;

  return (
    <PadNumberedSteps
      completedCount={completedCount}
      firstStepAction={
        stepOpen ? (
          <div className="flex w-full max-w-md flex-col items-stretch gap-3">
            <PadBankLinkingCallout />
            {selectBank}
            {onSendBlank ? <BlankPadAgreementButton onSendBlank={onSendBlank} sending={blankSending} /> : null}
          </div>
        ) : null
      }
    />
  );
}
